import { logAPIError } from '../../utils/errorLogger'
import type { MakeRequest } from './marketplaceApi'

/**
 * AI Assistant chat — RAG-powered chat backed by Ollama.
 *
 * Topics:
 *   - 'material_advisor' — material selection guidance (doctors + farms)
 *   - 'print_debug'      — print failure + marketplace error diagnosis (farms only)
 */
export type AssistantTopic = 'material_advisor' | 'print_debug' | 'girasoles_assistant'

export interface AssistantChatTurn {
  role: 'user' | 'assistant'
  content: string
}

export interface ConversationMessage {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  thinking?: string
  tokens_used?: number
  model_used?: string
}

export interface AssistantSource {
  index: number
  source: string
  heading: string
}

export interface AssistantChatResponse {
  role_used: 'doctor' | 'farm'
  topic: AssistantTopic
  model: string
  answer: string
  thinking?: string | null
  sources: AssistantSource[]
  latency_ms: number
  tokens_used: number
  total_cost: number
  ai_token_balance: number | null
}

export interface AssistantHealth {
  ok: boolean
  model: string
  model_available?: boolean
  available_models?: string[]
  error?: string
  knowledge_base: { sources: Record<string, number>; topics: string[] }
}

// ── Conversation persistence (farm-only) ────────────────────────────────────

export interface ConversationSummary {
  id: string
  farm_id: string
  kind: AssistantTopic
  title: string | null
  created_at: string
  updated_at: string
  last_message_preview: string | null
}

// ── API functions ────────────────────────────────────────────────────────────

export async function getAssistantHealth(makeRequest: MakeRequest): Promise<AssistantHealth> {
  try {
    return await makeRequest<AssistantHealth>('/api/assistant/health', { method: 'GET' })
  } catch (error) {
    const msg = error instanceof Error ? error.message : 'failed to load assistant health'
    logAPIError('/api/assistant/health', 'GET', 0, msg)
    throw error
  }
}

export interface PageContext {
  window_type: string
  tab_title?: string
  project_name?: string
  open_windows?: Array<{ window_type: string; tab_id: string; label: string }>
}

export async function sendAssistantChat(
  makeRequest: MakeRequest,
  topic: AssistantTopic,
  messages: AssistantChatTurn[],
  opts?: { model?: string; conversation_id?: string; page_context?: PageContext },
): Promise<AssistantChatResponse> {
  try {
    return await makeRequest<AssistantChatResponse>('/api/assistant/chat', {
      method: 'POST',
      body: JSON.stringify({ topic, messages, ...opts }),
    })
  } catch (error) {
    const msg = error instanceof Error ? error.message : 'failed to send assistant chat'
    logAPIError('/api/assistant/chat', 'POST', 0, msg)
    throw error
  }
}

export async function listConversations(
  makeRequest: MakeRequest,
  kind: AssistantTopic,
  isDoctor = false,
): Promise<ConversationSummary[]> {
  const base = isDoctor ? '/api/assistant/doctor/conversations' : '/api/assistant/conversations'
  try {
    return await makeRequest<ConversationSummary[]>(
      `${base}?kind=${kind}&limit=50`,
      { method: 'GET' },
    )
  } catch (error) {
    const msg = error instanceof Error ? error.message : 'failed to list conversations'
    logAPIError(base, 'GET', 0, msg)
    throw error
  }
}

export async function createConversation(
  makeRequest: MakeRequest,
  kind: AssistantTopic,
  title?: string,
  isDoctor = false,
): Promise<ConversationSummary> {
  const base = isDoctor ? '/api/assistant/doctor/conversations' : '/api/assistant/conversations'
  try {
    return await makeRequest<ConversationSummary>(base, {
      method: 'POST',
      body: JSON.stringify({ kind, title }),
    })
  } catch (error) {
    const msg = error instanceof Error ? error.message : 'failed to create conversation'
    logAPIError(base, 'POST', 0, msg)
    throw error
  }
}

export async function renameConversation(
  makeRequest: MakeRequest,
  id: string,
  title: string,
  isDoctor = false,
): Promise<ConversationSummary> {
  const base = isDoctor ? '/api/assistant/doctor/conversations' : '/api/assistant/conversations'
  try {
    return await makeRequest<ConversationSummary>(`${base}/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ title }),
    })
  } catch (error) {
    const msg = error instanceof Error ? error.message : 'failed to rename conversation'
    logAPIError(`${base}/${id}`, 'PUT', 0, msg)
    throw error
  }
}

export async function deleteConversation(
  makeRequest: MakeRequest,
  id: string,
  isDoctor = false,
): Promise<void> {
  const base = isDoctor ? '/api/assistant/doctor/conversations' : '/api/assistant/conversations'
  try {
    await makeRequest<void>(`${base}/${id}`, { method: 'DELETE' })
  } catch (error) {
    const msg = error instanceof Error ? error.message : 'failed to delete conversation'
    logAPIError(`${base}/${id}`, 'DELETE', 0, msg)
    throw error
  }
}

export async function getConversationMessages(
  makeRequest: MakeRequest,
  id: string,
  isDoctor = false,
): Promise<ConversationMessage[]> {
  const base = isDoctor ? '/api/assistant/doctor/conversations' : '/api/assistant/conversations'
  try {
    return await makeRequest<ConversationMessage[]>(
      `${base}/${id}/messages`,
      { method: 'GET' },
    )
  } catch (error) {
    const msg = error instanceof Error ? error.message : 'failed to get messages'
    logAPIError(`${base}/${id}/messages`, 'GET', 0, msg)
    throw error
  }
}
